import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  MapIcon,
  HeartIcon,
  SparklesIcon,
  UsersIcon,
  SunIcon,
  HomeIcon,
  AcademicCapIcon,
  HandRaisedIcon,
  MusicalNoteIcon,  GlobeAltIcon,
  ArrowRightIcon
} from '@heroicons/react/24/outline';
import useSEO from '../hooks/useSEO';

interface Service {
  id: string;
  name: string;
  description: string;
  icon: React.ElementType;
  image: string;
  highlights: string[];
  duration: string;
  price: string;
  difficulty: 'Easy' | 'Moderate' | 'Challenging';
  category: 'Adventure' | 'Culture' | 'Wellness' | 'Food' | 'Entertainment' | 'Photography';
}

const services: Service[] = [  {
    id: 'hiking-trekking',
    name: 'Guided Hiking & Trekking',
    description: 'Explore the pristine trails around Bethanchok with experienced local guides. Discover hidden waterfalls, terraced fields, and breathtaking Himalayan viewpoints.',
    icon: MapIcon,
    image: '/images/services/hiking.webp',
    highlights: ['Local expert guides', 'Multiple trail options', 'Himalayan viewpoints', 'Photography stops'],
    duration: '2-6 hours',
    price: 'From $10 per person',
    difficulty: 'Moderate',
    category: 'Adventure'
  },  {
    id: 'yoga-meditation',
    name: 'Morning Yoga & Meditation',
    description: 'Start your day with peaceful yoga sessions and guided meditation in our serene garden, surrounded by mountains and nature sounds.',
    icon: HeartIcon,
    image: '/images/services/yoga.webp',
    highlights: ['Certified instructor', 'Mountain backdrop', 'All levels welcome', 'Meditation garden'],
    duration: '1-1.5 hours',
    price: 'From $8 per session',
    difficulty: 'Easy',
    category: 'Wellness'
  },  {
    id: 'village-cultural-tour',
    name: 'Traditional Village Tours',
    description: 'Immerse yourself in authentic Nepali village life. Visit local homes, temples, and workshops while learning about traditional customs and daily routines.',
    icon: UsersIcon,
    image: '/images/services/village_tour.jpg',
    highlights: ['Family visits', 'Temple ceremonies', 'Traditional crafts', 'Cultural exchange'],
    duration: '3-4 hours',
    price: 'From $11 per person',
    difficulty: 'Easy',
    category: 'Culture'
  },{
    id: 'cooking-classes',
    name: 'Traditional Cooking Classes',
    description: 'Learn the art of Nepali cuisine from our experienced host family. Prepare dal bhat, momos, and local delicacies using traditional methods.',
    icon: SparklesIcon,
    image: '/images/services/cooking.webp',
    highlights: ['Hands-on experience', 'Family recipes', 'Fresh ingredients', 'Take recipes home'],
    duration: '2-3 hours',
    price: 'From $11 per person',
    difficulty: 'Easy',
    category: 'Food'
  },  {
    id: 'homestay-experience',
    name: 'Authentic Homestay Living',
    description: 'Experience genuine Nepali hospitality by staying with our local family. Participate in daily activities and truly live like a local.',
    icon: HomeIcon,
    image: '/images/services/homestay.jpg',
    highlights: ['Family integration', 'Daily activities', 'Cultural immersion', 'Shared meals'],
    duration: 'Per night',
    price: 'From $11 per night',
    difficulty: 'Easy',
    category: 'Culture'
  },  {
    id: 'campfire-folk-music',
    name: 'Evening Campfire & Folk Music',
    description: 'Gather around the traditional fire pit for an evening of Nepali folk songs, local stories, and cultural performances under the starlit sky.',
    icon: MusicalNoteIcon,
    image: '/images/services/campfire.jpg',
    highlights: ['Live folk music', 'Traditional stories', 'Cultural dance', 'Stargazing'],
    duration: '2-3 hours',
    price: 'From $11 per person',
    difficulty: 'Easy',
    category: 'Entertainment'
  },{
    id: 'sunrise-bird-watching',
    name: 'Himalayan Sunrise & Bird Watching',
    description: 'Experience the magical Himalayan sunrise while observing diverse bird species in their natural habitat. Perfect for nature lovers and photographers.',
    icon: SunIcon,
    image: '/images/services/sunrise.jpeg',
    highlights: ['Himalayan sunrise', 'Bird identification', 'Photography guide', 'Binoculars provided'],
    duration: '3-4 hours',
    price: 'From $9 per person',
    difficulty: 'Moderate',
    category: 'Photography'
  },  {
    id: 'language-cultural-exchange',
    name: 'Nepali Language & Cultural Exchange',
    description: 'Learn basic Nepali phrases and cultural etiquette while sharing your own culture with our host family and local community.',
    icon: AcademicCapIcon,
    image: '/images/services/culture.jpg',
    highlights: ['Basic Nepali lessons', 'Cultural etiquette', 'Language practice', 'Cultural sharing'],
    duration: '1-2 hours',
    price: 'From $6 per session',
    difficulty: 'Easy',
    category: 'Culture'
  },  {
    id: 'organic-farming',
    name: 'Organic Farming Experience',
    description: 'Get hands-on experience with traditional organic farming methods. Learn about sustainable agriculture and help with seasonal farming activities.',
    icon: HandRaisedIcon,
    image: '/images/services/farming.jpg',
    highlights: ['Organic methods', 'Seasonal activities', 'Farm-to-table', 'Sustainability focus'],
    duration: '2-4 hours',
    price: 'From $8 per person',
    difficulty: 'Moderate',
    category: 'Culture'
  },  {
    id: 'mountain-meditation',
    name: 'Mountain Peak Meditation',
    description: 'Experience profound peace with guided meditation sessions at scenic mountain viewpoints. Connect with nature in the most serene settings.',
    icon: GlobeAltIcon,
    image: '/images/services/mountain_meditation.jpg',
    highlights: ['Guided meditation', 'Mountain viewpoints', 'Nature connection', 'Mindfulness practice'],
    duration: '2-3 hours',
    price: 'From $10 per person',
    difficulty: 'Moderate',
    category: 'Wellness'
  }
];

export default function Services() {
  const [selectedCategory, setSelectedCategory] = useState<string>('All');

  const categories = ['All', 'Adventure', 'Culture', 'Wellness', 'Food', 'Entertainment', 'Photography'];

  const filteredServices = selectedCategory === 'All'    ? services 
    : services.filter(service => service.category === selectedCategory);

  const servicesStructuredData = {
    "@context": "https://schema.org",
    "@type": "Service",
    "provider": {
      "@type": "LodgingBusiness",
      "name": "Bethanchok Homestay"
    },
    "serviceType": "Cultural Tourism Services",
    "description": "Authentic Nepali cultural experiences including trekking, yoga, meditation, traditional crafts, and cultural immersion activities",
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "Homestay Experience Services",
      "itemListElement": [
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Trekking and Hiking",
            "description": "Guided mountain trails and hiking experiences"
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service", 
            "name": "Yoga and Meditation",
            "description": "Peaceful yoga sessions in natural mountain setting"
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Cultural Experiences",
            "description": "Traditional Nepali cultural activities and learning"
          }
        }
      ]
    }  };

  // SEO Hook  
  useSEO({
    title: "Services & Experiences | Bethanchok Homestay | Nepal Cultural Tourism Activities",
    description: "Discover authentic Nepali experiences at Bethanchok Homestay. Enjoy trekking, yoga, meditation, traditional crafts, cultural immersion, and mountain adventures. Authentic Nepal cultural tourism services.",
    keywords: "Nepal cultural experiences, Bethanchok activities, Nepal trekking, yoga Nepal, meditation retreat Nepal, traditional crafts Nepal, Nepal cultural tourism, authentic Nepal activities, mountain hiking Nepal, Nepal adventure services, cultural immersion Nepal",
    image: "https://bethanchokstay.com/images/services/cultural-experience.jpg",
    url: "https://bethanchokstay.com/services",
    structuredData: servicesStructuredData
  });

  return (
    <div className="bg-white py-24 sm:py-32 page-transition">
      <div className="mx-auto max-w-7xl px-6 lg:px-8">{/* Header */}
        <div className="mx-auto max-w-2xl lg:text-center mb-16">
          <h2 className="text-base font-semibold leading-7 text-primary-600">Authentic Experiences</h2>
          <p className="mt-2 text-3xl font-bold tracking-tight text-neutral-900 sm:text-4xl">
            Discover Bethanchok's Rich Heritage
          </p>
          <p className="mt-6 text-lg leading-8 text-neutral-600">
            Immerse yourself in genuine Nepali culture through our carefully curated homestay experiences. 
            From traditional crafts to mountain adventures, create memories that will last a lifetime.
          </p>
        </div>        {/* Service Categories Filter */}
        <div className="flex flex-wrap justify-center gap-3 mb-12">
          {categories.map((category) => (
            <button
              key={category}
              onClick={() => setSelectedCategory(category)}
              className={`px-4 py-2 text-sm font-medium rounded-full border transition-colors duration-200 ${
                selectedCategory === category
                  ? 'bg-primary-600 text-white border-primary-600'
                  : 'border-primary-200 text-primary-700 hover:bg-primary-50'
              }`}
            >
              {category}
              {category !== 'All' && (
                <span className="ml-1 text-xs opacity-75">
                  ({services.filter(s => s.category === category).length})
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Results Counter */}
        <div className="text-center mb-8">
          <p className="text-sm text-neutral-600">
            Showing {filteredServices.length} {filteredServices.length === 1 ? 'service' : 'services'}
            {selectedCategory !== 'All' && ` in ${selectedCategory}`}
          </p>
        </div>{/* Services Grid */}
        <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3">
          {filteredServices.map((service, index) => (
            <Link 
              key={service.id} 
              to={`/services/${service.id}`}
              className="group relative bg-white rounded-2xl shadow-soft overflow-hidden hover:shadow-soft-lg transition-all duration-300 transform hover:-translate-y-1"
            >
              {/* Image */}
              <div className="aspect-[4/3] overflow-hidden relative">
                <img
                  src={service.image}
                  alt={service.name}
                  className="h-full w-full object-cover object-center group-hover:scale-105 transition-transform duration-300"
                />
                {/* Category Badge */}
                <div className="absolute top-3 left-3 bg-white/90 backdrop-blur-sm rounded-lg px-2 py-1">
                  <span className="text-xs font-semibold text-primary-600">{service.category}</span>
                </div>
                {/* Icon overlay */}
                <div className="absolute top-3 right-3 p-2 bg-white/90 backdrop-blur-sm rounded-lg">
                  <service.icon className="h-5 w-5 text-primary-600" />
                </div>
                {/* Difficulty Badge */}
                <div className="absolute bottom-3 left-3 bg-neutral-800/80 backdrop-blur-sm rounded-lg px-2 py-1">
                  <span className={`text-xs font-medium ${
                    service.difficulty === 'Easy' ? 'text-green-400' :
                    service.difficulty === 'Moderate' ? 'text-yellow-400' : 'text-red-400'
                  }`}>
                    {service.difficulty}
                  </span>
                </div>
              </div>
              
              {/* Content */}
              <div className="p-5">
                <div className="flex items-start justify-between mb-2">
                  <h3 className="text-lg font-bold text-neutral-900 group-hover:text-primary-600 transition-colors duration-200 leading-tight">
                    {service.name}
                  </h3>
                </div>                  <p className="text-neutral-600 mb-4 leading-relaxed text-sm line-clamp-3">{service.description}</p>
                
                {/* Price and Duration */}
                <div className="flex items-center justify-between mb-4 p-3 bg-neutral-50 rounded-lg">
                  <div className="flex items-center space-x-4">
                    <div>
                      <p className="text-xs text-neutral-500 uppercase tracking-wide font-medium">Duration</p>
                      <p className="text-sm font-semibold text-neutral-900">{service.duration}</p>
                    </div>
                    <div className="h-8 w-px bg-neutral-300"></div>
                    <div>
                      <p className="text-xs text-neutral-500 uppercase tracking-wide font-medium">Price</p>
                      <p className="text-sm font-semibold text-primary-600">{service.price}</p>
                    </div>
                  </div>
                </div>
                
                {/* Highlights - Compact Design */}
                <div className="space-y-2">
                  <div className="grid grid-cols-2 gap-1">
                    {service.highlights.slice(0, 4).map((highlight, idx) => (
                      <div 
                        key={idx} 
                        className="bg-primary-50/50 border border-primary-100 rounded-md px-2 py-1 text-xs font-medium text-primary-700"
                      >
                        <div className="flex items-center">
                          <div className="w-1 h-1 bg-primary-500 rounded-full mr-1.5 flex-shrink-0" />
                          <span className="truncate">{highlight}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Learn More Button */}
                <div className="mt-4 pt-3 border-t border-neutral-100">
                  <div className="flex items-center text-primary-600 group-hover:text-primary-700 font-semibold text-sm">
                    <span>Learn More</span>
                    <ArrowRightIcon className="h-4 w-4 ml-1 group-hover:translate-x-1 transition-transform duration-200" />
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>        {/* CTA Section */}
        <div className="mt-20 bg-gradient-to-r from-primary-50 to-secondary-50 rounded-3xl p-8 lg:p-12">
          <div className="mx-auto max-w-2xl text-center">
            <h3 className="text-2xl font-bold tracking-tight text-neutral-900 sm:text-3xl">
              Create Your Perfect Homestay Experience
            </h3>
            <p className="mt-4 text-lg leading-8 text-neutral-600">
              Combine multiple services or let us customize an experience package 
              tailored to your interests and preferences. Every guest receives personalized attention.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                to="/booking"
                className="w-full sm:w-auto rounded-lg bg-primary-600 px-6 py-3 text-sm font-semibold text-white shadow-sm hover:bg-primary-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600 transition-colors duration-200 text-center"
              >
                Book Your Experience
              </Link>
              <Link
                to="/contact"
                className="w-full sm:w-auto text-sm font-semibold leading-6 text-neutral-900 hover:text-primary-600 transition-colors duration-200 text-center"
              >
                Ask Questions <span aria-hidden="true">→</span>
              </Link>
            </div>
          </div>
        </div>

        {/* Testimonial Section */}
        <div className="mt-20 bg-white py-16 rounded-3xl shadow-soft">
          <div className="mx-auto max-w-2xl text-center">
            <div className="flex justify-center mb-6">
              <div className="flex items-center space-x-1">
                {[...Array(5)].map((_, i) => (
                  <svg key={i} className="h-5 w-5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                  </svg>
                ))}
              </div>
            </div>
            <blockquote className="text-lg font-medium text-neutral-900 italic">
              "The authentic homestay experience at Bethanchok was life-changing. From cooking traditional meals 
              to hiking through pristine mountain trails, every moment was filled with genuine warmth and cultural discovery."
            </blockquote>
            <figcaption className="mt-4">
              <div className="text-sm font-semibold text-primary-600">Sarah Thompson</div>
              <div className="text-sm text-neutral-500">Travel Blogger, UK</div>
            </figcaption>
          </div>
        </div>
      </div>
    </div>
  );
}
